import { createRouter, createWebHistory } from "vue-router";
import { queryClient } from "@/libs/queryClient";
import * as extensionAuth from "@/libs/extensionAuth";
import LoginView from "@/views/LoginView.vue";
import DashboardView from "@/views/dashboard/DashboardView.vue";
import PatientListView from "@/views/patients/PatientListView.vue";
import ProductListView from "@/views/products/ProductListView.vue";
import PatientRecordView from "@/views/patients/PatientRecordView.vue";
import ProductRecordView from "@/views/products/ProductRecordView.vue";
import TicketListView from "@/views/tickets/TicketListView.vue";
import TicketRecordView from "@/views/tickets/TicketRecordView.vue";
import ReceivableListView from "@/views/receivables/ReceivableListView.vue";
import PaymentListView from "@/views/payments/PaymentListView.vue";
import TRTDashboardView from "@/views/trt/TRTDashboardView.vue";

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView
  },
  {
    path: '/patients',
    name: 'Patient List',
    component: PatientListView
  },
  {
    path: '/patients/:id/:tab?',
    name: 'Patient Details',
    component: PatientRecordView
  },
  {
    path: '/tickets',
    name: 'Ticket List',
    component: TicketListView
  },
  {
    path: '/tickets/:id/:tab?',
    name: 'Ticket Details',
    component: TicketRecordView
  },
  {
    path: '/products',
    name: 'Product List',
    component: ProductListView
  },
  {
    path: '/products/:id/:tab?',
    name: 'Product Details',
    component: ProductRecordView
  },
  {
    path: '/receivables',
    name: 'Receivable List',
    component: ReceivableListView
  },
  {
    path: '/payments',
    name: 'Payment List',
    component: PaymentListView
  },
  {
    path: '/trt',
    name: 'TRT',
    component: TRTDashboardView
  },
]

const router = createRouter({
  routes: routes,
  history: createWebHistory()
})

// View-owned query keys: filter/search/sort/pagination state that a view writes
// into the URL and must be able to clear (e.g. wiping the search box removes
// ?search). These are NEVER persisted across navigation — otherwise the guard
// below re-attaches the page-load value the instant a view tries to clear it,
// making it impossible to unset.
const VIEW_OWNED_QUERY = ['search', 'filters', 'page', 'sortBy', 'sortOrder', 'date']

// Query params present on the initial page load (e.g. ?extension=true,
// ?embed=true). SPA navigations and redirects drop the query string, so we
// re-attach these on every navigation to keep the mode flags in the URL.
const persistentQuery = captureInitialQuery()

function captureInitialQuery() {
  try {
    const params = new URLSearchParams(window.location.search)
    return Object.fromEntries(
      [...params].filter(([key]) => !VIEW_OWNED_QUERY.includes(key))
    )
  } catch {
    return {}
  }
}

router.beforeEach((to) => {
  const keys = Object.keys(persistentQuery)
  if (keys.length === 0) return true
  const missing = keys.some((key) => to.query[key] === undefined)
  if (!missing) return true
  // Existing params win, so an explicit value is never clobbered.
  return { path: to.path, query: { ...persistentQuery, ...to.query }, hash: to.hash }
})

let authVerified = false

router.beforeEach(async (to) => {
  if (to.path === '/login' || to.path === '/') {
    authVerified = false
    queryClient.removeQueries({ queryKey: ['user'] })
    return true
  }

  if (authVerified) return true

  try {
    const opts = { headers: {} }
    if (extensionAuth.isExtensionMode()) {
      // Extension mode: authenticate the guard's /auth/me probe via the bearer
      // header, never cookies. No token yet => 401 => redirect to /login.
      opts.credentials = 'omit'
      opts.headers['X-Extension-Mode'] = 'true'
      // Lazily pull the token from the extension the first time a protected
      // route needs it (boot is no longer blocked on this round-trip).
      let token = extensionAuth.getToken()
      if (!token) {
        token = await extensionAuth.loadToken()
      }
      if (token) {
        opts.headers['Authorization'] = `Bearer ${token}`
      }
    } else {
      opts.credentials = 'include'
    }
    const res = await fetch(process.env.VUE_APP_API + '/auth/me', opts)
    if (res.ok) {
      const data = await res.json()
      queryClient.setQueryData(['user'], data.user)
      authVerified = true
      return true
    }
    return '/login'
  } catch {
    return '/login'
  }
})

export default router;